'use client';

import BDImage from '@shared/components/elements/bd-image';
import { GOLD_CLUB_INFO, essentialMenu, menu } from '@shared/lib/constants';
import { MetalTypes, ringOptions } from '@shared/lib/search/product';
import { Menu } from '@shared/lib/shopify/types';
import { DiamondVariant } from '@shared/lib/shopify/types/shopify-native';
import { rPercentage } from '@shared/lib/utils/formats';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { TfiAngleRight } from 'react-icons/tfi';

interface MainMenuProps {
  onClose?: () => void;
}

export default function MainMenu({ onClose }: MainMenuProps) {
  return (
    <div className="flex h-full flex-col justify-between">
      <ul className="flex flex-col justify-center gap-5 pt-5">
        {menu.map((item) => (
          <li key={item.title}>
            <Link
              href={item.path}
              className="font-menu text-bd-primary/80 hover:text-bd-primary flex max-w-[12em] items-center text-base capitalize"
              onClick={onClose}
            >
              <span className="flex-1 pr-2">{item.title}</span>
              <span className="ml-auto text-xs">
                <TfiAngleRight />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {/* <Link href="/" className="">
        <VerticalLogo className="text-bd-primary h-auto max-w-[12em] py-10" />
      </Link> */}

      <Link
        href="/club#join-club"
        className="text-bd-primary flex w-full flex-col items-start gap-y-2 px-4 py-4"
      >
        <h4 className="whitespace-nowrap text-xs font-medium uppercase">
          Join the Bonded Club Today
        </h4>
        <p className="font-primary font-header whitespace-nowrap text-5xl">
          {rPercentage(GOLD_CLUB_INFO.discount)} off
        </p>
        <p className="text-xs font-normal uppercase"> your diamond and jewelry order</p>
      </Link>
    </div>
  );
}

const HeaderMenuItem = ({
  index,
  item,
  cutVariantLists,
}: {
  index: number;
  item: Menu;
  cutVariantLists: Record<string, DiamondVariant[]>;
}) => {
  const pathname = usePathname();
  const [active, setActive] = useState(pathname === item.path);

  useEffect(() => {
    setActive(pathname === item.path);
  }, [pathname, item.path]);

  return (
    <li
      className={clsx(
        'group flex flex-row items-center justify-center',
        index > 2 && 'hidden md:flex'
      )}
    >
      <Link
        href={item.path}
        className={clsx(
          'font-menu group-hover:text-bd-primary ring-bd-primary group-hover:border-bd-primary items-center whitespace-nowrap border-b border-transparent px-4 pb-1 text-xs font-normal uppercase ring-0 transition duration-300 ease-in-out',
          {
            'text-bd-primary border-b': active,
          }
        )}
      >
        {item.title}
      </Link>
      {item.title === 'Diamonds' && <ShopDiamondsMenu cutVariantLists={cutVariantLists} />}
      {item.title === 'Engagement Rings' && <ShopRingsMenu cutVariantLists={cutVariantLists} />}
      {item.title === 'Pendants' && <ShopPendantsMenu cutVariantLists={cutVariantLists} />}
    </li>
  );
};

export function HorizontalMenu({
  cutVariantLists,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
}) {
  return (
    <section className="relative mt-4 w-full md:block lg:mt-6">
      <div className="mx-auto flex flex-row items-center justify-between px-8 md:max-w-[65em]">
        {essentialMenu.map((item, index) => (
          <HeaderMenuItem
            key={item.title}
            index={index}
            item={item}
            cutVariantLists={cutVariantLists}
          />
        ))}
      </div>
    </section>
  );
}

export function ShopDiamondsMenu({
  cutVariantLists,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
}) {
  const diamondMenu = [
    {
      title: 'View All',
      path: '/shop/diamonds#customize',
    },
    {
      title: 'View Best Selling',
      path: '/shop/diamonds#featured',
    },
    {
      title: 'View Diamond FAQ',
      path: '/shop/diamonds#faqs',
    },
  ];
  return (
    <nav
      className="border-bd-contrast absolute left-0 top-full hidden w-full flex-col justify-end border-b pt-4 duration-300 ease-in-out group-hover:md:block"
      style={{ backgroundColor: 'var(--navbar-bg)' }}
    >
      <div className="border-bd-contrast w-full border-t" />
      <section className="xl:mx-22 mx-auto flex max-w-screen-xl flex-row items-start justify-center gap-x-12 pb-6 pt-4 md:mx-10 lg:mx-16">
        <div className="flex flex-col gap-y-2 whitespace-nowrap">
          {diamondMenu.map((item) => (
            <Link key={item.title} href={item.path} className="text-xs font-medium uppercase">
              {item.title}
            </Link>
          ))}
        </div>
        <div className="flex max-w-[55em] flex-col gap-y-2">
          <h4 className="text-xs font-medium uppercase">Shop by Diamond Shape</h4>
          <div className="flex flex-row gap-x-2">
            {Object.entries(cutVariantLists).map(([shape, variants]) => (
              <Link
                key={shape}
                href={`/shop/diamonds?shape=${shape}#customize`}
                className="text-xs font-normal capitalize"
              >
                <div className="flex flex-col items-center">
                  <div className="aspect-square w-full rounded-sm">
                    <BDImage
                      src={variants[0]!.image}
                      alt={shape}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>

                  {shape}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </nav>
  );
}

export function ShopRingsMenu({
  cutVariantLists,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
}) {
  const ringMenu = [
    {
      title: 'View All',
      path: '/shop/rings#all',
    },
    {
      title: 'View Best Selling',
      path: '/shop/rings#featured',
    },
    {
      title: 'View Engagement Rings FAQ',
      path: '/shop/rings#faqs',
    },
  ];
  return (
    <nav
      className="border-bd-contrast absolute left-0 top-full hidden w-full flex-col justify-end border-b pt-4 duration-300 ease-in-out group-hover:md:block"
      style={{ backgroundColor: 'var(--navbar-bg)' }}
    >
      <div className="border-bd-contrast w-full border-t" />
      <section className="@container xl:mx-22 mx-auto flex max-w-screen-xl flex-row items-start justify-center gap-x-12 pb-6 pt-4 md:mx-10 lg:mx-16">
        <div className="flex flex-col gap-y-2">
          {ringMenu.map((item) => (
            <Link key={item.title} href={item.path} className="text-xs font-medium uppercase">
              {item.title}
            </Link>
          ))}
        </div>
        <div className="flex flex-col gap-y-2 whitespace-nowrap">
          <h4 className="text-xs font-medium uppercase">Shop by Diamond Shape</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {Object.entries(cutVariantLists).map(([shape]) => (
              <Link
                key={shape}
                href={`/shop/rings?cut=${shape.toLowerCase()}&filterByCart=false#all`}
                className="text-xs font-normal capitalize"
              >
                {shape}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-y-2 whitespace-nowrap">
          <h4 className="text-xs font-medium uppercase">Shop by Metal</h4>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2">
            {MetalTypes.map((metal) => (
              <Link
                key={metal}
                href={`/shop/rings?metal=${metal}&filterByCart=false#all`}
                className="text-xs font-normal capitalize"
              >
                {metal.replace('_', ' ')}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-y-2 whitespace-nowrap">
          <h4 className="text-xs font-medium uppercase">Shop by Ring Type</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {Object.entries(ringOptions).map(([key, value]) => (
              <Link
                key={key}
                href={`/shop/rings?ring=${key}&filterByCart=false#all`}
                className="text-xs font-normal capitalize"
              >
                {value.label}
              </Link>
            ))}
          </div>
        </div>

        <Link
          href="/club#join-club"
          className="bg-bd-secondary @[65em]:flex -mb-8 -mt-4 hidden h-full flex-col items-start gap-y-2 px-4 pb-16 pt-4"
          style={{ backgroundColor: 'var(--navbar-contrast)' }}
        >
          <h4 className="whitespace-nowrap text-xs font-medium uppercase">
            Join the Bonded Club Today
          </h4>
          <p className="font-primary font-header whitespace-nowrap text-5xl">
            {rPercentage(GOLD_CLUB_INFO.discount)} off
          </p>
          <p className="text-xs font-normal uppercase"> your diamond and jewelry order</p>
        </Link>
      </section>
    </nav>
  );
}

export function ShopPendantsMenu({
  cutVariantLists,
}: {
  cutVariantLists: Record<string, DiamondVariant[]>;
}) {
  const pendantMenu = [
    {
      title: 'View All',
      path: '/shop/pendants#all',
    },
    {
      title: 'View Best Selling',
      path: '/shop/pendants#featured',
    },
    {
      title: 'View Pendants FAQ',
      path: '/shop/pendants#faqs',
    },
  ];
  return (
    <nav
      className="border-bd-contrast absolute left-0 top-full hidden w-full flex-col justify-end border-b pt-4 duration-300 ease-in-out group-hover:md:block"
      style={{ backgroundColor: 'var(--navbar-bg)' }}
    >
      <div className="border-bd-contrast w-full border-t" />
      <section className="xl:mx-22 mx-auto flex max-w-screen-xl flex-row items-start justify-center gap-x-8 pb-6 pt-4 md:mx-10 lg:mx-16">
        <div className="flex flex-col gap-y-2">
          {pendantMenu.map((item) => (
            <Link key={item.title} href={item.path} className="text-xs font-medium uppercase">
              {item.title}
            </Link>
          ))}
        </div>
        <div className="flex flex-col gap-y-2 whitespace-nowrap">
          <h4 className="text-xs font-medium uppercase">Shop by Diamond Shape</h4>
          <div className="grid grid-cols-4 gap-x-4 gap-y-2">
            {Object.entries(cutVariantLists).map(([shape]) => (
              <Link
                key={shape}
                href={`/shop/pendants?cut=${shape.toLowerCase()}&filterByCart=false#all`}
                className="text-xs font-normal capitalize"
              >
                {shape}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-y-2 whitespace-nowrap">
          <h4 className="text-xs font-medium uppercase">Shop by Metal</h4>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2">
            {MetalTypes.map((metal) => (
              <Link
                key={metal}
                href={`/shop/pendants?metal=${metal}&filterByCart=false#all`}
                className="text-xs font-normal capitalize"
              >
                {metal.replace('_', ' ')}
              </Link>
            ))}
          </div>
        </div>
        <Link
          href="/club#join-club"
          className="-mb-8 -mt-4 ml-2 flex flex-col items-start gap-y-2 px-4 pb-10 pt-4"
          style={{ backgroundColor: 'var(--navbar-contrast)' }}
        >
          <h4 className="whitespace-nowrap text-xs font-medium uppercase">
            Join the Bonded Club Today
          </h4>
          <p className="font-primary font-header whitespace-nowrap text-5xl">
            {rPercentage(GOLD_CLUB_INFO.discount)} off
          </p>
          <p className="text-xs font-normal uppercase"> your diamond and jewelry order</p>
        </Link>
      </section>
    </nav>
  );
}
